import type {
  GuestContactMethod,
  guestQuoteRequestResultSchema,
  ProductSizeOption,
  ShippingFeeSettings,
} from '@repo/api-contracts';
import type { z } from 'zod';
import {
  addDestinationError,
  blankToNull,
  checkQuoteRequestDestinationRules,
  DESTINATION_LIST_MESSAGE,
  type DestinationFieldErrors,
  destinationErrorEntry,
  destinationListMessage,
  isQuoteRequestSharedField,
  type MutableDestinationErrors,
  parseQuoteQuantity,
  QUOTE_REQUEST_INVALID_INPUT_MESSAGE,
  QUOTE_REQUEST_SHARED_FIELD_KEYS,
  type QuotePriceTier,
  type QuoteRequestRuleContext,
  type QuoteRequestSharedFieldKey,
  quoteRequestFieldMessage,
  SIZE_OPTIONS_CHANGED_MESSAGE,
  type SizeFieldValues,
  type SizeQuantityRow,
} from '@/entities/quote';
import {
  firstErrorPerField,
  getErrorMessage,
  getServerFieldErrors,
  getTrpcErrorCode,
  type ServerFieldErrors,
} from '@/shared/lib';

// 비회원 견적 요청 제출의 공용 검증·칸 오류 매핑 — 디자인 견적(`useSubmitGuestQuoteRequest`)과 일반 견적
// (`useSubmitGuestPlainQuoteRequest`)이 함께 쓴다. 두 요청은 디자인 문서·인쇄 방식 칸만 다르다.

/** 접수 결과(접수번호·견적 링크 경로)와 고른 연락 방법 — 완료 화면이 연락 안내 문구를 고른다. */
export type GuestQuoteRequestReceipt = z.infer<typeof guestQuoteRequestResultSchema> & {
  readonly contactMethod: GuestContactMethod;
};

/** 비회원 배송지 카드(SUPER-86 D8) — 우편번호·기본 주소(주소 검색 결과)·수량만. 받는 분·연락처·상세 주소는 주문할 때 받는다. */
export interface GuestDestinationValues {
  readonly postalCode: string;
  readonly address1: string;
  readonly quantity: string;
  readonly sizes: readonly SizeQuantityRow[];
}

/** 배송지 카드 한 장 — 회원 폼 목록 훅(`useDestinationList`)의 항목과 같은 모양(key 는 오류를 붙일 카드 이름). */
interface GuestDestinationDraft {
  readonly key: string;
  readonly value: GuestDestinationValues;
}

/** 두 요청이 함께 받는 폼 입력 — 수량·날짜·연락처도 입력창 문자열 그대로 받고 검증은 safeParse 가 한다. */
export interface GuestQuoteRequestCommonValues {
  readonly sizeFields: SizeFieldValues;
  readonly productId: string;
  readonly colorOptionId: string;
  readonly destinations: readonly GuestDestinationDraft[];
  readonly desiredDate: string;
  readonly companyName: string;
  readonly contactName: string;
  readonly memo: string;
  readonly contactMethod: GuestContactMethod;
  /** 두 칸 모두 화면에 남아 있을 수 있지만 고른 방법의 값만 보낸다. */
  readonly email: string;
  readonly phone: string;
  readonly privacyConsent: boolean;
}

/** 칸 오류 — `quantity` 는 총 수량(합계 규칙), `destinations` 는 배송지 묶음 자체 오류다. */
export type GuestQuoteRequestFieldKey =
  | QuoteRequestSharedFieldKey
  | 'destinations'
  | 'email'
  | 'phone'
  | 'privacyConsent';

export type GuestQuoteRequestFieldErrors = Partial<Record<GuestQuoteRequestFieldKey, string>>;

/** 카드 key → 그 카드의 칸 오류(회원 요청과 같은 모양). */
export type GuestQuoteRequestDestinationErrors = Readonly<Record<string, DestinationFieldErrors>>;

export interface UseSubmitGuestQuoteRequestOptions {
  readonly sizeOptions: readonly ProductSizeOption[];
  readonly minOrderQty: number;
  /** 임시 견적과 같은 구간 단가 — 총 수량 × 단가 + 배송비가 견적 금액 상한을 넘는지 미리 본다. */
  readonly priceTiers: readonly QuotePriceTier[];
  /** 요청 폼 조회의 배송비 설정 — 임시 견적과 같은 배송비로 금액 상한을 본다(제출 때는 서버가 다시 읽는다). */
  readonly shippingFeeSettings: ShippingFeeSettings;
  /** 희망 납기일 "한국 내일" 판정 기준 시각. 기본은 실제 시계, 테스트는 고정 시계를 넣는다. */
  readonly now?: () => Date;
  /** 비회원 키 — 에디터 이미지 업로드와 같은 키여야 서버가 이미지 소유를 확인한다(설계 G6). 테스트는 고정 값을 넣는다. */
  readonly readGuestKey?: () => string;
}

export interface UseSubmitGuestQuoteRequestResult<TValues> {
  readonly fieldErrors: GuestQuoteRequestFieldErrors;
  readonly destinationErrors: GuestQuoteRequestDestinationErrors;
  readonly formError: string | null;
  readonly isPending: boolean;
  /** 성공하면 접수 결과, 검증·서버 거절이면 null. */
  readonly submit: (values: TValues) => Promise<GuestQuoteRequestReceipt | null>;
}

export interface GuestQuoteRequestSubmissionErrors {
  readonly fieldErrors: GuestQuoteRequestFieldErrors;
  readonly destinationErrors: GuestQuoteRequestDestinationErrors;
  readonly formError: string | null;
}

/** 계약 스키마 safeParse 결과 — 디자인·일반 두 스키마의 결과를 같은 모양으로 받는다. */
export type GuestQuoteRequestParseResult<TInput> =
  | { readonly success: true; readonly data: TInput }
  | {
      readonly success: false;
      readonly error: {
        readonly issues: readonly {
          readonly path: readonly PropertyKey[];
          readonly code: string;
          readonly message?: string;
        }[];
      };
    };

export interface GuestQuoteRequestValidation<TInput> extends GuestQuoteRequestSubmissionErrors {
  readonly input: TInput | null;
}

/** 비회원만 받는 칸(연락처·동의) — 공용 칸 문구는 `@/entities/quote` 가 회원 요청과 함께 쓴다. */
const GUEST_ONLY_FIELD_KEYS = [
  'email',
  'phone',
  'privacyConsent',
] as const satisfies readonly GuestQuoteRequestFieldKey[];

type GuestOnlyFieldKey = (typeof GUEST_ONLY_FIELD_KEYS)[number];

/** 화면 칸 하나에 붙는 오류 키 — 배송지 묶음(`destinations`)·카드 칸은 따로 다룬다. */
type GuestFormFieldKey = QuoteRequestSharedFieldKey | GuestOnlyFieldKey;

const GUEST_INVALID_MESSAGES: Readonly<Record<GuestOnlyFieldKey, string>> = {
  email: '이메일 주소를 확인해 주세요.',
  phone: '휴대폰 번호를 확인해 주세요. (예: 010-1234-5678)',
  privacyConsent: '개인정보 수집·이용에 동의해 주세요.',
};

/** 고른 연락 칸이 비어 있을 때 — "확인해 주세요" 대신 "입력해 주세요". */
const EMPTY_CONTACT_MESSAGES = {
  email: '이메일 주소를 입력해 주세요.',
  phone: '휴대폰 번호를 입력해 주세요.',
} as const;

const FORM_ERROR_FALLBACK = '상담 요청에 실패했습니다.';
const DESIGN_INVALID_MESSAGE =
  '디자인 정보를 확인할 수 없어요. 에디터로 돌아가 디자인을 확인해 주세요.';
/** 비회원 요청 한도(설계 G16) — 서버 문구와 무관하게 같은 안내를 보여 준다. */
const RATE_LIMITED_MESSAGE = '잠시 후 다시 시도해 주세요.';

interface ValidationIssue {
  readonly code: string;
  readonly path: readonly PropertyKey[];
}

export const realClock = (): Date => new Date();

const isSharedFieldIn = (
  key: PropertyKey,
  sharedKeys: readonly QuoteRequestSharedFieldKey[],
): key is QuoteRequestSharedFieldKey =>
  typeof key === 'string' && (sharedKeys as readonly string[]).includes(key);

/** `sharedKeys` 는 그 요청이 화면에 보여 주는 공용 칸이다 — 목록에 없는 공용 칸은 칸 오류로 보내지 않는다. */
const isFieldKey = (
  key: PropertyKey,
  sharedKeys: readonly QuoteRequestSharedFieldKey[],
): key is GuestFormFieldKey =>
  isSharedFieldIn(key, sharedKeys) ||
  (typeof key === 'string' && (GUEST_ONLY_FIELD_KEYS as readonly string[]).includes(key));

/** 연락처는 판별 유니온이라 path 두 번째 조각(email·phone)이 칸 이름이다. */
const fieldKeyOf = (
  path: readonly PropertyKey[],
  sharedKeys: readonly QuoteRequestSharedFieldKey[],
): GuestFormFieldKey | null => {
  const key = path[0] === 'contact' ? path[1] : path[0];
  return key !== undefined && isFieldKey(key, sharedKeys) ? key : null;
};

const messageFor = (
  key: GuestFormFieldKey,
  issue: ValidationIssue,
  values: GuestQuoteRequestCommonValues,
): string => {
  if (isQuoteRequestSharedField(key)) return quoteRequestFieldMessage(key, issue.code);
  if (key === 'email' && values.email.trim() === '') return EMPTY_CONTACT_MESSAGES.email;
  if (key === 'phone' && values.phone.trim() === '') return EMPTY_CONTACT_MESSAGES.phone;
  return GUEST_INVALID_MESSAGES[key];
};

/** 고른 방법의 값만 연락처로 싣는다 — 다른 칸에 적어 둔 값은 보내지 않는다(설계 G12). */
const toContact = (values: GuestQuoteRequestCommonValues): unknown =>
  values.contactMethod === 'EMAIL'
    ? { method: 'EMAIL', email: values.email }
    : { method: 'PHONE', phone: values.phone };

/** 공용 칸을 계약 입력 모양으로 — 빈 선택 항목은 null, 수량은 정수 아니면 NaN(스키마가 거부). */
export const toGuestQuoteRequestCommonInput = (
  values: GuestQuoteRequestCommonValues,
  guestKey: string,
) => ({
  guestKey,
  productId: values.productId,
  colorOptionId: values.colorOptionId,
  destinations: values.destinations.map(({ value }) => ({
    postalCode: value.postalCode,
    address1: value.address1,
    quantity: parseQuoteQuantity(value.quantity),
    ...(value.sizes.length > 0
      ? {
          sizes: value.sizes.map((row) => ({
            sizeOptionId: row.sizeOptionId,
            quantity: parseQuoteQuantity(row.quantity),
          })),
        }
      : {}),
  })),
  desiredDate: blankToNull(values.desiredDate),
  companyName: values.companyName,
  contactName: values.contactName,
  memo: values.memo,
  contact: toContact(values),
  privacyConsent: values.privacyConsent,
});

const hasAnyError = (errors: GuestQuoteRequestSubmissionErrors): boolean =>
  Object.keys(errors.fieldErrors).length > 0 ||
  Object.keys(errors.destinationErrors).length > 0 ||
  errors.formError !== null;

/**
 * safeParse 결과 + 스키마 밖 규칙 → 칸 오류. `sharedKeys` 는 그 요청이 보여 주는 공용 칸이다 — 목록에 없는 칸(상품 id·비회원 키 등)의
 * 스키마 오류는 일반 폼 오류다.
 */
export const validateGuestQuoteRequest = <TInput>(
  parsed: GuestQuoteRequestParseResult<TInput>,
  values: GuestQuoteRequestCommonValues,
  context: QuoteRequestRuleContext,
  shippingFeeSettings: ShippingFeeSettings,
  sharedKeys: readonly QuoteRequestSharedFieldKey[] = QUOTE_REQUEST_SHARED_FIELD_KEYS,
  sizeOptions: readonly ProductSizeOption[] = [],
): GuestQuoteRequestValidation<TInput> => {
  const schemaErrors: GuestQuoteRequestFieldErrors = {};
  const destinationErrors: MutableDestinationErrors = {};
  let formError: string | null = null;

  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const key = fieldKeyOf(issue.path, sharedKeys);
      const entry = destinationErrorEntry(
        issue.path,
        issue.code,
        values.destinations,
        issue.message,
      );
      if (entry !== null) {
        // 사이즈 상품의 수량은 파생값이다. 빈 줄·줄 수량 오류는 보이는 sizes 칸에서 안내한다.
        if (sizeOptions.length > 0 && entry.field === 'quantity') continue;
        addDestinationError(destinationErrors, entry.cardKey, entry.field, entry.message);
      } else if (issue.path[0] === 'destinations') {
        // 목록 자체 오류(개수·합계) — 10곳 초과는 한도 문구, 그 밖은 일반 문구.
        schemaErrors.destinations ??= destinationListMessage(issue.code);
      } else if (key === null) {
        formError ??=
          issue.path[0] === 'design' ? DESIGN_INVALID_MESSAGE : QUOTE_REQUEST_INVALID_INPUT_MESSAGE;
      } else if (schemaErrors[key] === undefined) {
        schemaErrors[key] = messageFor(key, issue, values);
      }
    }
  }

  const rules = checkQuoteRequestDestinationRules(
    values,
    sizeOptions,
    shippingFeeSettings,
    context,
  );
  const fieldErrors: GuestQuoteRequestFieldErrors = { ...rules.totalErrors, ...schemaErrors };
  if (rules.sizeTotalError !== null) fieldErrors.quantity = rules.sizeTotalError;
  if (rules.allocationError !== null) fieldErrors.destinations ??= rules.allocationError;
  for (const [cardKey, message] of Object.entries(rules.destinationSizeErrors)) {
    addDestinationError(destinationErrors, cardKey, 'sizes', message);
  }

  const errors = { fieldErrors, destinationErrors, formError };
  return { ...errors, input: hasAnyError(errors) || !parsed.success ? null : parsed.data };
};

/**
 * 서버 검증(fieldErrors)은 flatten 이라 연락처 오류가 `contact` 한 키로 온다 — 고른 방법의 칸에 일반 문구로 붙인다.
 * 배송지는 회원 요청과 같다 — `destinations` 한 키는 배송지 묶음 오류, 도메인 검사의 점 키(`destinations.0.postalCode`, 교차 조정 R3)는
 * 제출한 카드 순서로 그 카드에 우리 문구로 붙인다. 동의는 동의 칸, 디자인 문서는 에디터 안내, 그 밖에 칸으로 못 보내는 키(guestKey 등)는
 * 일반 폼 오류로 바꾼다.
 */
const mapServerFieldErrors = (
  errors: ServerFieldErrors,
  values: GuestQuoteRequestCommonValues,
  sharedKeys: readonly QuoteRequestSharedFieldKey[],
): GuestQuoteRequestSubmissionErrors => {
  const fieldErrors: GuestQuoteRequestFieldErrors = {
    ...firstErrorPerField(errors, sharedKeys),
  };
  const destinationErrors: MutableDestinationErrors = {};
  const contactKey = values.contactMethod === 'EMAIL' ? 'email' : 'phone';
  let formError: string | null = null;

  for (const key of Object.keys(errors)) {
    const entry = destinationErrorEntry(key, 'server', values.destinations);
    if (entry?.field === 'sizes') {
      // 로컬 검사를 통과한 사이즈를 서버가 거절했다 — 폼을 연 뒤 사이즈 옵션이 바뀌었다. 사이즈 줄이 숨은 폼에서도 보이게
      // 배송지 묶음에도 안내한다(회원 요청과 같다).
      addDestinationError(destinationErrors, entry.cardKey, 'sizes', SIZE_OPTIONS_CHANGED_MESSAGE);
      fieldErrors.destinations ??= SIZE_OPTIONS_CHANGED_MESSAGE;
    } else if (entry !== null) {
      addDestinationError(destinationErrors, entry.cardKey, entry.field, entry.message);
    } else if (key === 'destinations') {
      fieldErrors.destinations = DESTINATION_LIST_MESSAGE;
    } else if (key === 'contact') {
      fieldErrors[contactKey] ??= GUEST_INVALID_MESSAGES[contactKey];
    } else if (key === 'privacyConsent') {
      fieldErrors.privacyConsent ??= GUEST_INVALID_MESSAGES.privacyConsent;
    } else if (key === 'design') {
      formError = DESIGN_INVALID_MESSAGE;
    } else if (!isSharedFieldIn(key, sharedKeys)) {
      formError ??= QUOTE_REQUEST_INVALID_INPUT_MESSAGE;
    }
  }

  return { fieldErrors, destinationErrors, formError };
};

/**
 * 제출 거절 → 화면 오류. 한도 초과(TOO_MANY_REQUESTS)는 "잠시 후 다시 시도해 주세요.", 칸으로 보낼 서버 필드 오류가 있으면 그 칸에,
 * 그 밖의 서버 거절은 서버 문구를 폼 오류로 보여 준다.
 */
export const guestQuoteRequestSubmitErrors = (
  error: unknown,
  values: GuestQuoteRequestCommonValues,
  sharedKeys: readonly QuoteRequestSharedFieldKey[] = QUOTE_REQUEST_SHARED_FIELD_KEYS,
): GuestQuoteRequestSubmissionErrors => {
  if (getTrpcErrorCode(error) === 'TOO_MANY_REQUESTS') {
    return { fieldErrors: {}, destinationErrors: {}, formError: RATE_LIMITED_MESSAGE };
  }
  const serverFieldErrors = getServerFieldErrors(error);
  const mapped =
    serverFieldErrors === null ? null : mapServerFieldErrors(serverFieldErrors, values, sharedKeys);
  if (mapped !== null && hasAnyError(mapped)) return mapped;
  return {
    fieldErrors: {},
    destinationErrors: {},
    formError: getErrorMessage(error, FORM_ERROR_FALLBACK),
  };
};
